import { useEffect, useState } from "react";
import { LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { fmtDate, money } from "../../lib/format";
import { PageHeader, Loading, StatusBadge, Money, Modal, Field, Table, Empty } from "../../components/common";

const STATUSES = ["OPEN", "IN_PROGRESS", "AWAITING_DOCUMENTS", "RECONSTRUCTION", "RESOLVED", "CLOSED"];

export default function AdminRescue() {
  const [cases, setCases] = useState(null);
  const [active, setActive] = useState(null);
  const [staff, setStaff] = useState([]);
  const [note, setNote] = useState("");

  const load = () => {
    API.get("/admin/rescue-cases").then((r) => setCases(r.data.cases));
    API.get("/admin/tasks").then((r) => setStaff(r.data.staff)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, update, msg) => {
    try {
      await API.patch(`/admin/rescue-cases/${id}`, update);
      if (msg) toast.success(msg);
      const { data } = await API.get("/admin/rescue-cases");
      setCases(data.cases);
      if (active) setActive(data.cases.find((c) => c.id === active.id) || null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!cases) return <Loading label="Loading rescue cases" />;

  return (
    <div data-testid="admin-rescue">
      <PageHeader title="AZ Tax Rescue Dashboard" sub="Past-due reconstruction cases — timelines, estimates, and recovery plans." />

      {cases.length === 0 ? <Empty title="No rescue cases" msg="Public Tax Rescue submissions appear here automatically." /> : (
        <Table headers={["Case #", "Business", "Owner", "Months Behind", "Tax Types", "Est. Exposure", "Status", "Assigned", "Created"]} testid="rescue-table">
          {cases.map((c) => (
            <tr key={c.id} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" onClick={() => setActive(c)} data-testid={`rescue-row-${c.case_number}`}>
              <td className="px-4 py-2.5 font-mono text-xs font-bold text-copper">{c.case_number}</td>
              <td className="px-4 py-2.5 font-semibold text-navy">{c.business_name} {c.is_demo && <span className="ml-1 rounded bg-gold/20 px-1 text-[9px] font-bold text-amber-700">DEMO</span>}</td>
              <td className="px-4 py-2.5 text-sm">{c.owner_name}<div className="text-xs text-slate-400">{c.email}</div></td>
              <td className="px-4 py-2.5 money font-semibold">{c.months_behind}</td>
              <td className="px-4 py-2.5 text-xs">{(c.tax_types || []).join(", ")}</td>
              <td className="px-4 py-2.5 money font-bold text-rose-700">{c.estimated_total_cents ? money(c.estimated_total_cents) : "—"}</td>
              <td className="px-4 py-2.5"><StatusBadge status={c.status} /></td>
              <td className="px-4 py-2.5 text-xs">{staff.find((s) => s.id === c.assigned_to)?.name || "Unassigned"}</td>
              <td className="px-4 py-2.5 text-xs text-slate-500">{fmtDate(c.created_at)}</td>
            </tr>
          ))}
        </Table>
      )}

      <Modal open={!!active} onClose={() => setActive(null)} title={active ? `${active.case_number} — ${active.business_name}` : ""} wide>
        {active && (
          <div className="space-y-5" data-testid="rescue-detail">
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              {[["Owner", active.owner_name], ["Phone", active.phone || "—"], ["EIN", active.ein_masked || "—"], ["TPT #", active.tpt_number_masked || "—"]].map(([k, v]) => (
                <div key={k} className="rounded border border-slate-200 px-3 py-2"><div className="text-[10px] uppercase tracking-widest text-slate-400">{k}</div><div className="font-semibold text-slate-700">{v}</div></div>
              ))}
            </div>

            {active.notes && <p className="rounded bg-slate-50 p-3 text-sm text-slate-600">{active.notes}</p>}
            {active.documents?.length > 0 && (
              <div>
                <div className="label-gov">Uploaded Documents</div>
                <ul className="text-sm text-slate-600">{active.documents.map((d, i) => <li key={i}>· {d.filename} <span className="text-xs text-slate-400">({d.category})</span></li>)}</ul>
              </div>
            )}

            {active.reconstruction?.length > 0 && (
              <div>
                <div className="label-gov">Reconstruction Timeline {!active.estimates_verified && <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 normal-case">ESTIMATES — NOT YET VERIFIED</span>}</div>
                <div className="space-y-2">
                  {active.reconstruction.map((r, i) => (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2.5" data-testid={`recon-month-${i}`}>
                      <div>
                        <div className="font-semibold text-navy">{r.label}</div>
                        <div className="text-xs text-slate-400">Gross {money(r.gross_cents)} · Est. tax {money(r.estimated_tax_cents)} · Pen. {money(r.penalties_cents)} · Int. {money(r.interest_cents)}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="money font-bold text-rose-700">{money(r.balance_cents)}</span>
                        <StatusBadge status={r.filing_status.includes("PAID") && !r.filing_status.includes("UNPAID") ? "PAID" : r.filing_status.includes("UNPAID") ? "PAST_DUE" : "PAST_DUE"} />
                        <span className="text-xs text-slate-500">{r.filing_status}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between rounded-md border-2 border-copper bg-copper-50 px-4 py-3">
                  <span className="font-bold text-copper">ESTIMATED TOTAL EXPOSURE</span>
                  <span className="money text-xl font-bold text-copper" data-testid="rescue-total">{money(active.estimated_total_cents)}</span>
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Status">
                <select className="input-gov" value={active.status} onChange={(e) => patch(active.id, { status: e.target.value }, "Status updated")} data-testid="rescue-status">
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Assign To">
                <select className="input-gov" value={active.assigned_to || ""} onChange={(e) => patch(active.id, { assigned_to: e.target.value || null }, "Case assigned")} data-testid="rescue-assign">
                  <option value="">Unassigned</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Estimates">
                <button className={`w-full rounded-md border px-3 py-2 text-sm font-semibold ${active.estimates_verified ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}
                  onClick={() => patch(active.id, { estimates_verified: !active.estimates_verified }, "Verification toggled")} data-testid="rescue-verify">
                  {active.estimates_verified ? "✓ Verified" : "Mark Verified"}
                </button>
              </Field>
            </div>

            <div className="flex gap-2">
              <input className="input-gov" placeholder="Internal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="rescue-note" />
              <button className="btn-navy" onClick={() => { patch(active.id, { internal_note: note }); setNote(""); }} data-testid="rescue-note-add">Add</button>
            </div>
            {(active.internal_notes || []).map((n, i) => (
              <div key={i} className="rounded bg-slate-50 p-2 text-xs text-slate-600"><strong>{n.by}</strong> · {fmtDate(n.at)} — {n.body}</div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
